// Main poll only — ranked party rows + footer CTA. Renders into the
// "standalone" main-column slot on the homepage.
//
// Auto-resize handshake: hunch.mt's embed posts its content height
// via postMessage({ type: 'hunch:embed:height', height }) on mount,
// on every data refresh, and on every layout change. We listen for
// that and resize the iframe so the widget never internally scrolls.

import { useEffect, useRef } from 'react';
import { HUNCH_ORIGIN, isHunchHeightMessage } from './origin';
import './HunchEmbed.css';

interface Props {
  /** Compact mode (`?top=2`) shows the top 2 parties with a
   *  "Show all" toggle — used when the widget sits in the narrow
   *  hero column. Set false to drop the top-2 cap and render the
   *  full party list — used in the polls_open below-hero band's
   *  right column, which is wide enough to hold the whole list. */
  compact?: boolean;
}

export default function HunchMainEmbed({ compact = true }: Props = {}) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const src = compact
    ? `${HUNCH_ORIGIN}/embed/election/main?top=2`
    : `${HUNCH_ORIGIN}/embed/election/main`;

  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== HUNCH_ORIGIN) return;
      if (!isHunchHeightMessage(e.data)) return;
      if (iframeRef.current) {
        iframeRef.current.style.height = `${e.data.height}px`;
      }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  return (
    <section className="hunch-embed" aria-label="Live Malta 2026 main election poll">
      <iframe
        ref={iframeRef}
        src={src}
        title="Live Malta 2026 election poll · powered by hunch.mt"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        className="hunch-embed__iframe hunch-embed__iframe--main"
      />
    </section>
  );
}
